package com.siriusxm.pia.views.unifiedaggregator

import com.siriusxm.pia.SXMUI
import com.siriusxm.pia.components.border
import org.jetbrains.compose.web.css.*

/**
 * StyleSheet for entity diff and comparison UI components.
 * Defines styles for field change rows, diff summaries, and side-by-side comparison views.
 */
object EntityDiffStyles : StyleSheet() {

    /**
     * Container for a single field change row.
     * Displays field path, old value, arrow, and new value inline with wrapping.
     */
    val diffRow by style {
        marginBottom(0.4.em)
        padding(0.5.em, 0.8.em)
        backgroundColor(SXMUI.containerHeaderBackgroundColor.value())
        borderRadius(6.px)
        display(DisplayStyle.Flex)
        flexWrap(FlexWrap.Wrap)
        alignItems(AlignItems.Center)
        gap(0.5.em)
    }

    /**
     * Field path label (e.g., "artist.name", "tracks[0].duration").
     */
    val diffFieldPath by style {
        fontWeight(600)
        fontFamily("monospace")
        color(SXMUI.subtleTextColor.value())
        minWidth(8.em)
    }

    /**
     * Old/removed value — shown in red with strikethrough.
     */
    val diffOldValue by style {
        color(SXMUI.stopColor.value())
        textDecoration("line-through")
        fontFamily("monospace")
        fontSize(SXMUI.smallFontSize.value())
        property("word-break", "break-word")
    }

    /**
     * New/added value — shown in green with bold weight.
     */
    val diffNewValue by style {
        color(SXMUI.goColor.value())
        fontWeight(600)
        fontFamily("monospace")
        fontSize(SXMUI.smallFontSize.value())
        property("word-break", "break-word")
    }

    /**
     * Placeholder text for null values ("(not set)", "(removed)").
     */
    val diffNullValue by style {
        color(SXMUI.subtleTextColor.value())
        fontStyle("italic")
        fontSize(SXMUI.smallFontSize.value())
    }

    /**
     * Arrow separator between old and new values.
     */
    val diffArrow by style {
        color(SXMUI.subtleTextColor.value())
        fontSize(1.1.em)
    }

    /**
     * Summary bar showing counts of changed, added, and removed fields.
     */
    val diffSummary by style {
        padding(0.8.em)
        backgroundColor(SXMUI.containerHeaderBackgroundColor.value())
        borderRadius(6.px)
        marginBottom(1.em)
        fontSize(0.9.em)
        fontWeight(600)
    }

    /**
     * Empty state message when no differences are found.
     */
    val diffEmpty by style {
        padding(1.em)
        textAlign("center")
        color(SXMUI.subtleTextColor.value())
        fontStyle("italic")
    }

    /**
     * Two-column grid layout for the side-by-side comparison view.
     */
    val sideBySideGrid by style {
        display(DisplayStyle.Grid)
        property("grid-template-columns", "1fr 1fr")
        gap(1.em)
    }

    /**
     * Panel container for each side of the side-by-side view.
     */
    val sideBySidePanel by style {
        border(1.px, LineStyle.Solid, SXMUI.borderDividerDefault.value())
        borderRadius(6.px)
        overflow("hidden")
    }

    /**
     * Header bar for each side-by-side panel ("Authorized Version" / "Rejected Update").
     */
    val sideBySidePanelHeader by style {
        padding(0.8.em)
        backgroundColor(SXMUI.containerHeaderBackgroundColor.value())
        border(0.px, 0.px, 1.px, 0.px, SXMUI.borderDividerDefault.value())
        fontWeight(600)
        fontSize(0.9.em)
    }

    /**
     * Scrollable content area within each side-by-side panel.
     */
    val sideBySidePanelContent by style {
        padding(0.8.em)
        maxHeight(600.px)
        overflow("auto")
    }

    /**
     * Column layout for CloudEvent field diffs with consistent spacing between cards.
     */
    val cloudEventDiffList by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(0.6.em)
    }

    /**
     * Container for a top-level field diff in the CloudEvent diff view.
     */
    val cloudEventDiffContainer by style {
        padding(0.8.em)
        backgroundColor(SXMUI.containerHeaderBackgroundColor.value())
        borderRadius(6.px)
    }

    /**
     * Title for a top-level primitive array diff, matching the standard CloudEvent diff title.
     */
    val primitiveArrayDiffTitle by style {
        fontWeight("bold")
        fontSize(1.cssRem)
        marginBottom(4.px)
    }

    /**
     * Summary row for primitive array diff badges and list size metadata.
     */
    val primitiveArrayDiffSummary by style {
        display(DisplayStyle.Flex)
        flexWrap(FlexWrap.Wrap)
        alignItems(AlignItems.Center)
        gap(0.5.em)
        margin(0.5.em, 0.px)
    }

    /**
     * Label for a primitive array diff section such as added or removed values.
     */
    val primitiveArrayDiffSectionTitle by style {
        color(SXMUI.subtleTextColor.value())
        fontSize(SXMUI.smallFontSize.value())
        fontWeight(600)
        marginTop(0.6.em)
        marginBottom(0.3.em)
    }

    /**
     * Wrapping row of badges for primitive array values.
     */
    val primitiveArrayDiffBadgeGroup by style {
        display(DisplayStyle.Flex)
        flexWrap(FlexWrap.Wrap)
        gap(4.px)
        marginBottom(0.4.em)
    }

    /**
     * Clickable row for showing or hiding long unchanged/order detail sections.
     */
    val primitiveArrayDiffToggle by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(0.4.em)
        cursor("pointer")
        color(SXMUI.subtleTextColor.value())
        fontSize(SXMUI.smallFontSize.value())
        marginTop(0.6.em)
        property("user-select", "none")
    }

    /**
     * Grid used to compare ordered primitive array positions when order changes.
     */
    val primitiveArrayOrderGrid by style {
        display(DisplayStyle.Grid)
        property("grid-template-columns", "5em minmax(0, 1fr) minmax(0, 1fr)")
        gap(4.px)
        marginTop(0.4.em)
        fontSize(SXMUI.smallFontSize.value())
    }

    /**
     * Monospace index cell for the ordered primitive array preview.
     */
    val primitiveArrayOrderIndex by style {
        color(SXMUI.subtleTextColor.value())
        fontFamily("monospace")
        padding(0.25.em, 0.4.em)
    }

    /**
     * Monospace value cell for the ordered primitive array preview.
     */
    val primitiveArrayOrderValue by style {
        fontFamily("monospace")
        padding(0.25.em, 0.4.em)
        property("word-break", "break-word")
    }

    /**
     * Container for the rejected entity JSON when no authorized version exists.
     */
    val rejectedEntityContainer by style {
        border(1.px, LineStyle.Solid, SXMUI.borderDividerDefault.value())
        borderRadius(6.px)
        padding(0.8.em)
        backgroundColor(SXMUI.containerHeaderBackgroundColor.value())
    }
}
